/**
 * Heartbeat Service (Hybrid Mode)
 * 
 * Tracks user activity and presence for analytics.
 * Optimized for minimal HTTP invocations via Supabase Realtime Presence.
 * 
 * Modes:
 * - PRIMARY: Supabase Realtime Presence (0 HTTP calls for online status)
 * - FALLBACK: HTTP heartbeat every 5 minutes (if Presence unavailable)
 * 
 * Features:
 * - Cross-tab singleton via BroadcastChannel (only one tab sends HTTP)
 * - Improved exponential backoff (60s min, 10 min max)
 * - Session lifecycle (start, heartbeat, end)
 * - Offline buffering via localStorage
 * - sendBeacon for guaranteed delivery on page close
 * 
 * IMPORTANT: Uses getCachedUserId() from AuthContext.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import { handleEdgeFunctionError } from './edgeFunctionErrorService';
import { getEdgeFunctionUrl, supabaseConfig } from '@/lib/config/environment';
import { getCachedUserId } from '@/contexts/AuthContext';
import { initPresence, isPresenceConnected, onPresenceStatusChange, stopPresence } from './presenceService';

// ============= TYPES =============

export interface HeartbeatEvent {
  type: 'session_start' | 'heartbeat' | 'session_end';
  timestamp: string;
  sessionId: string;
  isActive: boolean;
  metadata?: Record<string, unknown>;
}

export interface HeartbeatConfig {
  intervalMs: number;
  jitterPercent: number;
  usePresence: boolean;
  httpFallbackIntervalMs: number;
  rateLimitMinSec: number;
}

interface SessionState {
  sessionId: string;
  startedAt: number;
  lastActivity: number;
  isActive: boolean;
  eventBuffer: HeartbeatEvent[];
}

// ============= CONSTANTS =============

const STORAGE_KEY = 'heartbeat_session';
const BUFFER_KEY = 'heartbeat_buffer';
const CONFIG_KEY = 'heartbeat_config';
const BROADCAST_CHANNEL_NAME = 'heartbeat-sync';

// Default intervals (from feature flags)
const DEFAULT_INTERVAL_MS = 240000; // 4 minutes
const DEFAULT_HTTP_FALLBACK_INTERVAL_MS = 300000; // 5 minutes
const DEFAULT_JITTER_PERCENT = 30;
const DEFAULT_RATE_LIMIT_MIN_SEC = 120;

// Improved backoff parameters
const MIN_RETRY_INTERVAL_MS = 60000;  // 60 seconds minimum
const MAX_RETRY_INTERVAL_MS = 600000; // 10 minutes maximum

const ACTIVITY_TIMEOUT_MS = 60000; // 1 minute without activity = inactive
const MAX_BUFFER_SIZE = 50;
const MIN_FLUSH_EVENTS = 5;
const FLUSH_INTERVAL_MS = 300000; // Flush every 5 minutes (was 60s)

// Edge function endpoint
const HEARTBEAT_ENDPOINT = getEdgeFunctionUrl('heartbeat');

// ============= STATE =============

let session: SessionState | null = null;
let intervalId: ReturnType<typeof setTimeout> | null = null;
let config: HeartbeatConfig = {
  intervalMs: DEFAULT_INTERVAL_MS,
  jitterPercent: DEFAULT_JITTER_PERCENT,
  usePresence: true,
  httpFallbackIntervalMs: DEFAULT_HTTP_FALLBACK_INTERVAL_MS,
  rateLimitMinSec: DEFAULT_RATE_LIMIT_MIN_SEC,
};
let isInitialized = false;

// Rate limit cooldown state
let nextAllowedFlushAt = 0;
let consecutiveRateLimitErrors = 0;
let flushInFlight: Promise<void> | null = null;
let lastFlushAt = 0;

// Cross-tab singleton state
let broadcastChannel: BroadcastChannel | null = null;
let isActiveTab = false;
let presenceUnsubscribe: (() => void) | null = null;

// ============= HELPER FUNCTIONS =============

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function getDeviceId(): string {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
}

function getAnonymousId(): string {
  let anonId = localStorage.getItem('anonymous_id');
  if (!anonId) {
    anonId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('anonymous_id', anonId);
  }
  return anonId;
}

function calculateJitteredInterval(): number {
  const baseInterval = config.usePresence && isPresenceConnected()
    ? config.httpFallbackIntervalMs // 5 minutes when Presence active
    : config.intervalMs; // 4 minutes fallback
    
  const jitterRange = baseInterval * (config.jitterPercent / 100);
  const jitter = (Math.random() - 0.5) * 2 * jitterRange;
  return Math.max(MIN_RETRY_INTERVAL_MS, baseInterval + jitter);
}

/**
 * Improved backoff with minimum 60s, maximum 10 minutes
 */
function calculateBackoff(retryAfterSeconds?: number): number {
  if (retryAfterSeconds && retryAfterSeconds > 0) {
    // Use server-provided Retry-After + small jitter
    return Math.max(MIN_RETRY_INTERVAL_MS, retryAfterSeconds * 1000 + Math.random() * 5000);
  }
  // Exponential backoff: 60s → 120s → 240s → 480s → 600s (cap)
  const backoff = Math.min(
    MIN_RETRY_INTERVAL_MS * Math.pow(2, consecutiveRateLimitErrors),
    MAX_RETRY_INTERVAL_MS
  );
  return backoff + Math.random() * 5000; // Add jitter
}

// ============= CROSS-TAB SINGLETON =============

function initBroadcastChannel(): void {
  if (typeof BroadcastChannel === 'undefined') {
    isActiveTab = true;
    return;
  }

  try {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    
    // Request to become active tab
    broadcastChannel.postMessage({ type: 'HEARTBEAT_REQUEST', tabId: session?.sessionId });
    
    // Assume active after short delay if no response
    setTimeout(() => {
      if (!isActiveTab) {
        isActiveTab = true;
        broadcastChannel?.postMessage({ type: 'HEARTBEAT_ACTIVE', tabId: session?.sessionId });
        logger.debug('Heartbeat: This tab is active for HTTP heartbeat');
      }
    }, 100);
    
    broadcastChannel.onmessage = (event) => {
      const { type, tabId } = event.data;
      
      if (type === 'HEARTBEAT_REQUEST' && isActiveTab && tabId !== session?.sessionId) {
        broadcastChannel?.postMessage({ type: 'HEARTBEAT_ACTIVE', tabId: session?.sessionId });
      }
      
      if (type === 'HEARTBEAT_ACTIVE' && tabId !== session?.sessionId) {
        isActiveTab = false;
        logger.debug('Heartbeat: Another tab is active, this tab is passive');
      }
    };
  } catch {
    isActiveTab = true;
  }
}

// ============= STORAGE FUNCTIONS =============

function saveSession(): void {
  if (!session) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Silently fail
  }
}

function loadSession(): SessionState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function loadBuffer(): HeartbeatEvent[] {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveBuffer(buffer: HeartbeatEvent[]): void {
  try {
    localStorage.setItem(BUFFER_KEY, JSON.stringify(buffer.slice(-MAX_BUFFER_SIZE)));
  } catch {
    // Silently fail
  }
}

function clearBuffer(): void {
  try {
    localStorage.removeItem(BUFFER_KEY);
  } catch {
    // Silently fail
  }
}

// ============= CONFIG LOADING =============

async function loadConfig(): Promise<void> {
  try {
    // Check localStorage cache first
    const cached = localStorage.getItem(CONFIG_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.cachedAt < 3600000) { // 1 hour cache
        config = {
          intervalMs: parseInt(parsed.intervalMs, 10) || DEFAULT_INTERVAL_MS,
          jitterPercent: parseInt(parsed.jitterPercent, 10) || DEFAULT_JITTER_PERCENT,
          usePresence: parsed.usePresence !== 'false',
          httpFallbackIntervalMs: parseInt(parsed.httpFallbackIntervalMs, 10) || DEFAULT_HTTP_FALLBACK_INTERVAL_MS,
          rateLimitMinSec: parseInt(parsed.rateLimitMinSec, 10) || DEFAULT_RATE_LIMIT_MIN_SEC,
        };
        return;
      }
    }

    // Fetch from database
    const { data, error } = await supabase
      .from('app_settings')
      .select('key, value')
      .in('key', [
        'heartbeat_interval_ms', 
        'heartbeat_jitter_percent',
        'heartbeat.use_presence',
        'heartbeat.http_fallback_interval_ms',
        'heartbeat.rate_limit_min_sec',
      ]);

    if (error) throw error;

    const settingsMap = new Map(data?.map(r => [r.key, r.value]) || []);
    
    config = {
      intervalMs: parseInt(settingsMap.get('heartbeat_interval_ms') || String(DEFAULT_INTERVAL_MS), 10),
      jitterPercent: parseInt(settingsMap.get('heartbeat_jitter_percent') || String(DEFAULT_JITTER_PERCENT), 10),
      usePresence: settingsMap.get('heartbeat.use_presence') !== 'false',
      httpFallbackIntervalMs: parseInt(settingsMap.get('heartbeat.http_fallback_interval_ms') || String(DEFAULT_HTTP_FALLBACK_INTERVAL_MS), 10),
      rateLimitMinSec: parseInt(settingsMap.get('heartbeat.rate_limit_min_sec') || String(DEFAULT_RATE_LIMIT_MIN_SEC), 10),
    };

    // Cache config
    localStorage.setItem(CONFIG_KEY, JSON.stringify({
      ...config,
      cachedAt: Date.now(),
    }));

    logger.debug('Heartbeat config loaded', { 
      data: { 
        intervalMs: config.intervalMs, 
        usePresence: config.usePresence,
        httpFallbackIntervalMs: config.httpFallbackIntervalMs,
      },
      allowList: ['intervalMs', 'usePresence', 'httpFallbackIntervalMs'],
    });
  } catch (error) {
    logger.warn('Failed to load heartbeat config, using defaults', { error });
  }
}

// ============= EVENT HANDLING =============

function bufferEvent(event: HeartbeatEvent): void {
  const buffer = loadBuffer();
  buffer.push(event);
  saveBuffer(buffer);
}

function shouldSkipFlush(force = false): boolean {
  const now = Date.now();
  
  // Check cooldown from rate limit
  if (now < nextAllowedFlushAt) {
    return true;
  }
  
  // If forced (unload, session_end), don't skip
  if (force) return false;
  
  // Only active tab sends HTTP (cross-tab singleton)
  if (!isActiveTab) {
    return true;
  }
  
  // Check if enough time passed since last flush
  const buffer = loadBuffer();
  const timeSinceLastFlush = now - lastFlushAt;
  
  // With Presence active, be more conservative with HTTP
  const minFlushInterval = config.usePresence && isPresenceConnected()
    ? FLUSH_INTERVAL_MS // 5 minutes
    : 120000; // 2 minutes fallback
  
  if (buffer.length < MIN_FLUSH_EVENTS && timeSinceLastFlush < minFlushInterval) {
    return true;
  }
  
  return false;
}

async function flushBuffer(force = false): Promise<void> {
  // Prevent parallel flushes
  if (flushInFlight) {
    await flushInFlight;
    return;
  }
  
  // Check if we should skip
  if (shouldSkipFlush(force)) {
    return;
  }
  
  const buffer = loadBuffer();
  if (buffer.length === 0) return;

  const doFlush = async (): Promise<void> => {
    try {
      const userId = getCachedUserId();
      const sessionId = session?.sessionId;
      
      const response = await fetch(HEARTBEAT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseConfig.anonKey,
          'X-Session-Id': sessionId || '',
          'X-User-Id': userId || '',
        },
        body: JSON.stringify({
          events: buffer,
          userId,
          deviceId: getDeviceId(),
          anonymousId: getAnonymousId(),
          appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
        }),
      });

      if (response.ok) {
        clearBuffer();
        lastFlushAt = Date.now();
        consecutiveRateLimitErrors = 0;
        
        // Parse response for next_ping_after_sec
        try {
          const data = await response.json();
          if (data.next_ping_after_sec) {
            config.intervalMs = data.next_ping_after_sec * 1000;
          }
        } catch {
          // Ignore parse errors
        }
        
        logger.debug('Heartbeat buffer flushed', { 
          data: { eventCount: buffer.length },
          allowList: ['eventCount'],
        });
      } else if (response.status === 429 || response.status === 204) {
        // Rate limited or soft rate limit (204)
        consecutiveRateLimitErrors++;
        
        const retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10);
        const backoff = calculateBackoff(retryAfter);
        nextAllowedFlushAt = Date.now() + backoff;
        
        if (response.status === 429) {
          logger.warn('Heartbeat rate limited, backing off', {
            data: { backoffMs: backoff, retryAfter },
            allowList: ['backoffMs', 'retryAfter'],
          });
        } else {
          // 204 is soft limit, just debug log
          logger.debug('Heartbeat soft rate limit', {
            data: { backoffMs: backoff },
            allowList: ['backoffMs'],
          });
        }
      } else {
        handleEdgeFunctionError('heartbeat', {
          name: 'FunctionsHttpError',
          message: `HTTP ${response.status}`,
          context: { status: response.status },
        }, {
          route: window.location.pathname,
          additionalData: { eventCount: buffer.length },
        });
      }
    } catch (error) {
      handleEdgeFunctionError('heartbeat', error, {
        route: window.location.pathname,
        additionalData: { eventCount: buffer.length },
      });
    }
  };

  flushInFlight = doFlush();
  try {
    await flushInFlight;
  } finally {
    flushInFlight = null;
  }
}

function sendBeaconFlush(): void {
  const buffer = loadBuffer();
  if (buffer.length === 0) return;

  const now = Date.now();
  if (now < nextAllowedFlushAt - 30000) {
    return;
  }

  if (session) {
    buffer.push({
      type: 'session_end',
      timestamp: new Date().toISOString(),
      sessionId: session.sessionId,
      isActive: false,
    });
  }

  try {
    const payload = JSON.stringify({
      events: buffer,
      userId: getCachedUserId(),
      deviceId: getDeviceId(),
      anonymousId: getAnonymousId(),
    });

    const sent = navigator.sendBeacon(
      HEARTBEAT_ENDPOINT,
      new Blob([payload], { type: 'application/json' })
    );
    
    if (sent) {
      clearBuffer();
    }
  } catch {
    // Silently fail
  }
}

// ============= ACTIVITY TRACKING =============

function handleActivity(): void {
  if (!session) return;
  session.lastActivity = Date.now();
  session.isActive = true;
  saveSession();
}

function checkActivityStatus(): void {
  if (!session) return;
  const wasActive = session.isActive;
  session.isActive = Date.now() - session.lastActivity < ACTIVITY_TIMEOUT_MS;
  
  if (wasActive !== session.isActive) {
    saveSession();
  }
}

// ============= HEARTBEAT LOOP =============

function scheduleNextHeartbeat(): void {
  if (intervalId) {
    clearTimeout(intervalId);
  }

  const interval = calculateJitteredInterval();
  
  intervalId = setTimeout(async () => {
    checkActivityStatus();
    
    if (session) {
      bufferEvent({
        type: 'heartbeat',
        timestamp: new Date().toISOString(),
        sessionId: session.sessionId,
        isActive: session.isActive,
      });

      // Only flush if this is the active tab
      if (isActiveTab) {
        await flushBuffer();
      }
    }

    scheduleNextHeartbeat();
  }, interval);
}

// ============= PUBLIC API =============

/**
 * Initialize heartbeat service with hybrid Presence/HTTP mode
 */
export async function initHeartbeat(): Promise<void> {
  if (isInitialized) return;
  isInitialized = true;

  await loadConfig();
  
  // Initialize cross-tab singleton
  initBroadcastChannel();

  // Check for existing session
  const existingSession = loadSession();
  const sessionAge = existingSession ? Date.now() - existingSession.startedAt : Infinity;
  const maxSessionAge = 4 * 60 * 60 * 1000; // 4 hours

  if (existingSession && sessionAge < maxSessionAge) {
    session = existingSession;
    session.lastActivity = Date.now();
    session.isActive = true;
    logger.debug('Resumed heartbeat session', {
      data: { sessionId: session.sessionId.slice(0, 8) },
      allowList: ['sessionId'],
    });
  } else {
    session = {
      sessionId: generateSessionId(),
      startedAt: Date.now(),
      lastActivity: Date.now(),
      isActive: true,
      eventBuffer: [],
    };

    bufferEvent({
      type: 'session_start',
      timestamp: new Date().toISOString(),
      sessionId: session.sessionId,
      isActive: true,
      metadata: {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
      },
    });

    logger.debug('Started new heartbeat session', {
      data: { sessionId: session.sessionId.slice(0, 8) },
      allowList: ['sessionId'],
    });
  }

  saveSession();

  // Initialize Presence if enabled
  if (config.usePresence) {
    const presenceConnected = await initPresence();
    
    if (presenceConnected) {
      logger.info('Heartbeat: Presence mode active (0 HTTP for online status)');
    } else {
      logger.warn('Heartbeat: Presence failed, falling back to HTTP mode');
    }
    
    // Subscribe to Presence status changes
    presenceUnsubscribe = onPresenceStatusChange((online) => {
      if (!online) {
        // Presence disconnected, increase HTTP frequency temporarily
        logger.debug('Presence disconnected, increasing HTTP heartbeat frequency');
      }
    });
  }

  // Activity listeners (throttled)
  let activityTimeout: ReturnType<typeof setTimeout> | null = null;
  const throttledActivity = () => {
    if (activityTimeout) return;
    activityTimeout = setTimeout(() => {
      handleActivity();
      activityTimeout = null;
    }, 1000);
  };

  window.addEventListener('mousemove', throttledActivity, { passive: true });
  window.addEventListener('keydown', throttledActivity, { passive: true });
  window.addEventListener('click', throttledActivity, { passive: true });
  window.addEventListener('scroll', throttledActivity, { passive: true });
  window.addEventListener('touchstart', throttledActivity, { passive: true });

  // Visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (session) {
        session.isActive = false;
        saveSession();
      }
    } else {
      handleActivity();
    }
  });

  // Page unload
  window.addEventListener('beforeunload', sendBeaconFlush);
  window.addEventListener('pagehide', sendBeaconFlush);

  // Start heartbeat loop (HTTP fallback)
  scheduleNextHeartbeat();

  // Initial flush
  if (isActiveTab) {
    await flushBuffer(true);
  }
}

/**
 * Stop heartbeat service
 */
export function stopHeartbeat(): void {
  if (intervalId) {
    clearTimeout(intervalId);
    intervalId = null;
  }

  if (session) {
    bufferEvent({
      type: 'session_end',
      timestamp: new Date().toISOString(),
      sessionId: session.sessionId,
      isActive: false,
    });
  }

  if (presenceUnsubscribe) {
    presenceUnsubscribe();
    presenceUnsubscribe = null;
  }
  
  stopPresence();
  
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }

  flushBuffer(true);
  isInitialized = false;
}

/**
 * Get current session ID
 */
export function getSessionId(): string | null {
  return session?.sessionId || null;
}

/**
 * Get current config
 */
export function getHeartbeatConfig(): HeartbeatConfig {
  return { ...config };
}

/**
 * Force refresh config from database
 */
export async function refreshHeartbeatConfig(): Promise<void> {
  localStorage.removeItem(CONFIG_KEY);
  await loadConfig();
  
  if (isInitialized) {
    scheduleNextHeartbeat();
  }
}

/**
 * Check if Presence mode is active
 */
export function isPresenceModeActive(): boolean {
  return config.usePresence && isPresenceConnected();
}
